import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertCircle, Info, Plus, Trash2 } from "lucide-react";
import { TransportEntry } from "@/types/product/productStep.types";

interface TransportLegsFieldProps {
  transports: TransportEntry[];
  onChange: (transports: TransportEntry[]) => void;
  /** Options for the vehicle-type select; value === vehicleType string. */
  vehicleOptions: { value: string; label: string }[];
  loading?: boolean;
  /** When true, show validation styling/messages (i.e. the field has been touched). */
  showError?: boolean;
  error?: string;
  /** Called when any leg field is blurred — lets the parent mark the field touched. */
  onBlur?: () => void;
}

interface TransportLegRowProps {
  leg: TransportEntry;
  index: number;
  canRemove: boolean;
  vehicleOptions: { value: string; label: string }[];
  loading?: boolean;
  showError?: boolean;
  onChange: (leg: TransportEntry) => void;
  onRemove: () => void;
  onBlur: () => void;
}

/**
 * One transport leg: vehicle-type select + distance input + remove button.
 * Keeps the distance as local text state so decimals (e.g. "0.5") type smoothly,
 * while propagating a parsed number to the parent.
 */
function TransportLegRow({
  leg,
  index,
  canRemove,
  vehicleOptions,
  loading,
  showError,
  onChange,
  onRemove,
  onBlur,
}: TransportLegRowProps) {
  const { t } = useLanguage();
  const [distanceText, setDistanceText] = useState<string>(
    leg.distance !== undefined && leg.distance !== null && !Number.isNaN(leg.distance)
      ? String(leg.distance)
      : ""
  );

  // Rows are keyed by index, so React reuses a row instance when a leg is
  // removed/reordered and feeds it a different leg's props. Resync the local
  // text whenever the incoming distance no longer matches what the text holds.
  // The numeric comparison avoids clobbering in-progress input like "12." (which
  // parses to the same number already stored), keeping decimal typing smooth.
  useEffect(() => {
    const localRaw = distanceText.trim();
    const localNum = localRaw === "" ? undefined : parseFloat(localRaw);
    const localNorm = localNum === undefined || Number.isNaN(localNum) ? undefined : localNum;
    const propNorm =
      leg.distance === undefined || leg.distance === null || Number.isNaN(leg.distance) ? undefined : leg.distance;
    if (localNorm !== propNorm) {
      setDistanceText(propNorm === undefined ? "" : String(propNorm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leg.distance]);

  const vehicleInvalid = showError && !leg.vehicleType;
  const distanceInvalid = showError && !(Number(distanceText) > 0);

  return (
    <div className="rounded-md border border-gray-200 bg-white/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">
          {t("products.transportNumber", { number: index + 1 })}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500"
            aria-label={t("products.removeTransport")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Vehicle type */}
        <div className="space-y-2">
          <Label className="font-medium text-gray-700">{t("products.vehicleType")} *</Label>
          <SearchableSelect
            value={leg.vehicleType || ""}
            onChange={(value) => onChange({ ...leg, vehicleType: value })}
            onBlur={onBlur}
            options={vehicleOptions}
            loading={loading}
            placeholder={t("products.selectVehicleType")}
            className={vehicleInvalid ? "border-red-500" : ""}
          />
        </div>

        {/* Distance */}
        <div className="space-y-2">
          <Label className="font-medium text-gray-700">{t("products.distanceTraveled")} *</Label>
          <div className="relative">
            <Input
              type="number"
              min="0"
              step="0.1"
              value={distanceText}
              onChange={(e) => {
                const text = e.target.value;
                setDistanceText(text);
                const parsed = text === "" ? NaN : parseFloat(text);
                onChange({ ...leg, distance: Number.isNaN(parsed) ? (undefined as unknown as number) : parsed });
              }}
              onBlur={onBlur}
              placeholder="0.0"
              className={`h-11 pr-12 ${distanceInvalid ? "border-red-500" : "border-gray-300"}`}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
              km
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Repeatable transport legs for a transformation product (e.g. import via air + land).
 * Always renders at least one leg.
 */
export function TransportLegsField({
  transports,
  onChange,
  vehicleOptions,
  loading,
  showError,
  error,
  onBlur,
}: TransportLegsFieldProps) {
  const { t } = useLanguage();

  // Always present at least one (empty) leg to fill in.
  const legs: TransportEntry[] =
    transports && transports.length > 0 ? transports : [{ vehicleType: "", distance: undefined as unknown as number }];

  const updateLeg = (index: number, leg: TransportEntry) => {
    const next = legs.map((l, i) => (i === index ? leg : l));
    onChange(next);
  };

  const addLeg = () => {
    onChange([...legs, { vehicleType: "", distance: undefined as unknown as number }]);
  };

  const removeLeg = (index: number) => {
    const next = legs.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : [{ vehicleType: "", distance: undefined as unknown as number }]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="font-medium text-gray-700">{t("products.vehicleType")} & {t("products.distanceTraveled")}</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 text-gray-400 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{t("products.vehicleTypeHelp")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-3">
        {legs.map((leg, index) => (
          <TransportLegRow
            key={index}
            leg={leg}
            index={index}
            canRemove={legs.length > 1}
            vehicleOptions={vehicleOptions}
            loading={loading}
            showError={showError}
            onChange={(updated) => updateLeg(index, updated)}
            onRemove={() => removeLeg(index)}
            onBlur={() => onBlur?.()}
          />
        ))}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addLeg} className="gap-2">
        <Plus className="h-4 w-4" />
        {t("products.addTransport")}
      </Button>

      {showError && error && (
        <div className="flex items-center gap-1 text-sm text-red-500">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
